import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { CreditCard, Home, Shield, Wrench, Users, FileCheck, CheckCircle } from 'lucide-react';

const features = [
  {
    icon: CreditCard,
    title: 'Smart Rent Payment',
    benefits: [
      'Pay via M-Pesa, bank transfer, or wallet',
      'Instant PDF receipts',
      'Automatic late fee calculation',
    ],
    badge: 'No cash, no excuses, no disputes',
    gradient: 'from-blue-500 to-cyan-500',
    glowColor: 'group-hover:shadow-blue-500/20',
  },
  {
    icon: Home,
    title: 'Rent Mdogo Mdogo',
    subtitle: '(Pay in Comfortable Bites)',
    benefits: [
      'Pay KES 15,000/month instead of KES 40,000 lump sum',
      'Lender covers full rent to your agency on time, every time',
      'Build your credit profile with every payment',
    ],
    badge: 'Stretch your rent. Build your credit.',
    gradient: 'from-purple-500 to-pink-500',
    glowColor: 'group-hover:shadow-purple-500/20',
  },
  {
    icon: Shield,
    title: 'Insurance Marketplace',
    benefits: [
      'Browse & compare affordable policies',
      'Apply instantly & manage claims digitally',
      'Protect belongings without breaking the bank',
    ],
    badge: 'Affordable protection, always',
    gradient: 'from-emerald-500 to-green-500',
    glowColor: 'group-hover:shadow-emerald-500/20',
  },
  {
    icon: Wrench,
    title: 'Maintenance Management',
    benefits: [
      'Report issues with photos',
      'Set priority levels (urgent/normal/low)',
      'Track resolution in real-time',
    ],
    badge: 'Faster repairs, full accountability',
    gradient: 'from-orange-500 to-red-500',
    glowColor: 'group-hover:shadow-orange-500/20',
  },
  {
    icon: Users,
    title: 'Community Social Feed',
    benefits: [
      'Post, like, and comment with other renters',
      'Share experiences and real advice',
      'Find recommendations for services',
    ],
    badge: "You're never alone - 12M+ community",
    gradient: 'from-pink-500 to-rose-500',
    glowColor: 'group-hover:shadow-pink-500/20',
  },
  {
    icon: FileCheck,
    title: 'Digital Check-In/Out',
    benefits: [
      'Structured checklists & photo documentation',
      'Automated deposit calculation',
      'End deposit disputes forever',
    ],
    badge: 'Complete transparency, zero disputes',
    gradient: 'from-cyan-500 to-blue-500',
    glowColor: 'group-hover:shadow-cyan-500/20',
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.2 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

export default function FeaturesSection() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const orb1Y = useTransform(scrollYProgress, [0, 1], ['0%', '-20%']);
  const orb2Y = useTransform(scrollYProgress, [0, 1], ['0%', '20%']);

  return (
    <section ref={ref} className="py-24 relative overflow-hidden">
      {/* Background orbs with parallax */}
      <motion.div className="absolute top-20 left-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" style={{ y: orb1Y }}></motion.div>
      <motion.div className="absolute bottom-20 right-10 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" style={{ y: orb2Y }}></motion.div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-purple-500/5 to-pink-500/5 rounded-full blur-3xl"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <span className="inline-block px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-sm font-medium mb-6">
            One App. Everything You Need.
          </span>
          <h2 className="section-title">
            Stop juggling.{' '}
            <span className="text-gradient">Start living.</span>
          </h2>
          <p className="text-lg text-gray-300 max-w-3xl mx-auto mt-4">
            YAWOTE integrates payments, property management, insurance, and community 
            into a single, seamless mobile experience.
          </p>
        </motion.div>

        {/* Features Grid */}
        <motion.div
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {features.map((feature, idx) => {
            const IconComponent = feature.icon;
            return (
              <motion.div key={idx} variants={itemVariants} className="group relative">
                {/* Glow Effect */}
                <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} rounded-2xl blur-xl opacity-0 group-hover:opacity-20 transition-opacity duration-500`}></div>
                
                {/* Card */}
                <div className="relative card h-full flex flex-col backdrop-blur-sm">
                  {/* Icon */}
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                    <IconComponent className="text-white" size={28} />
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-bold text-white mb-1">{feature.title}</h3>
                  {feature.subtitle && (
                    <p className="text-sm text-blue-400 font-semibold mb-4">{feature.subtitle}</p>
                  )}

                  {/* Benefits */}
                  <ul className="space-y-3 mb-6 flex-1">
                    {feature.benefits.map((benefit, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-gray-300">
                        <CheckCircle size={16} className="text-blue-400 mt-0.5 flex-shrink-0" />
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Badge */}
                  <div className="pt-4 border-t border-white/5">
                    <p className="text-xs font-semibold text-blue-400/80 italic">{feature.badge}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Bottom Highlight */}
        <motion.div
          className="mt-16 p-8 md:p-12 rounded-2xl bg-gradient-to-r from-blue-600/10 via-purple-600/10 to-cyan-600/10 border border-blue-500/20 text-center"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.6, duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h4 className="text-2xl font-bold text-white mb-2">One Platform. Everything You Need.</h4>
          <p className="text-gray-300">
            From rent payments to insurance, maintenance to community—YAWOTE handles it all, 
            giving you back time and peace of mind.
          </p>
        </motion.div>
      </div>
    </section>
  );
}
